import type { Zone } from 'types/match'

import Modal from 'components/Modal'
import styled from 'styled-components'
import copy from 'copy.json'
import Button from 'components/Button'
import Badge from './Badge'

type Props = {
  isOpen: boolean
  zones: Zone[]
  onClose: () => void
}

const texts = copy.page.matches.zones_modal

export default function ZonesModal({ isOpen, zones, onClose }: Props) {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Wrapper>
        <h1>{texts.heading}</h1>

        <Content>
          <table>
            <thead>
              <tr>
                <th />
                <th>
                  <Badge secondary>{texts.consumption}</Badge>
                </th>
                <th>
                  <Badge secondary coverage>
                    {texts.coverage}
                  </Badge>
                </th>
                <th>
                  <Badge secondary shortage>
                    {texts.shortage}
                  </Badge>
                </th>
                <th>
                  <Badge secondary surplus>
                    {texts.surplus}
                  </Badge>
                </th>
              </tr>
            </thead>

            <tbody>
              {zones.map((zone, index) => (
                <tr key={index}>
                  <td>
                    <div>
                      <Badge secondary border>
                        {texts.zone} {index + 1}
                      </Badge>

                      <span>{texts.zones[zones.length - 1][index]}</span>
                    </div>
                  </td>

                  <td>
                    <div>
                      <Badge>
                        {zone.total_required} {copy.common.MWh}
                      </Badge>
                    </div>
                  </td>

                  <td>
                    <div>
                      <Badge coverage>
                        {zone.total_coverage} {copy.common.MWh}
                      </Badge>
                      <Badge>{Math.round(zone.coverage_percentage)}%</Badge>
                    </div>
                  </td>

                  <td>
                    <div>
                      <Badge shortage>
                        {zone.total_shortage} {copy.common.MWh}
                      </Badge>
                      <Badge>{Math.round(zone.shortage_percentage)}%</Badge>
                    </div>
                  </td>

                  <td>
                    <div>
                      <Badge surplus>
                        {zone.total_surplus} {copy.common.MWh}
                      </Badge>
                      <Badge>{Math.round(zone.surplus_percentage)}%</Badge>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Content>

        <Button onClick={onClose}>{texts.close_button}</Button>
      </Wrapper>
    </Modal>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;

  h1 {
    font: var(--600-24);
  }

  ${Button} {
    align-self: center;
    width: 100%;
    max-width: 420px;
  }
`

const Content = styled.div`
  display: flex;
  background: var(--very-light-blue);
  padding: 16px;
  overflow-x: auto;

  table {
    width: 100%;
    border-spacing: 20px;

    th {
      text-align: right;
    }

    td {
      text-align: left;
      vertical-align: top;

      :first-child > div {
        display: flex;
        flex-direction: column;
        gap: 8px;

        > span:last-child {
          max-width: 100px;
          color: var(--gray);
          font: var(--600-13);
        }
      }

      :not(:first-child) > div {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 8px;
      }
    }
  }
`
